"use client";

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from "react";

export type SavedAddress = {
  id: string;
  address: string;
  lat: number;
  lng: number;
  apartment?: string;
  notes?: string;
};

export type CustomerInfo = {
  name: string;
  address: string;
  lat?: number;
  lng?: number;
  apartment?: string;
  notes?: string;
  phone?: string;
  email?: string;
  mode?: "delivery" | "pickup";
  paymentMethod?: "ONLINE" | "IN_PERSON" | "CASH";
  selectedAddressId?: string;
};

// ---------------------------------------------------------------------------
// Generic localStorage hook using useSyncExternalStore
// - getServerSnapshot returns the default → no hydration mismatch
// - getSnapshot reads localStorage on the first CLIENT render (sync, before paint)
// ---------------------------------------------------------------------------
function useLocalStorage<T>(
  key: string,
  defaultValue: T
): [T, (updater: T | ((prev: T) => T)) => void] {
  const defaultRef = useRef(defaultValue);
  const cacheRef = useRef<{ raw: string | null; parsed: T }>({
    raw: null,
    parsed: defaultValue,
  });

  const getSnapshot = useCallback((): T => {
    const raw = localStorage.getItem(key);
    if (raw !== cacheRef.current.raw) {
      try {
        cacheRef.current = { raw, parsed: raw ? JSON.parse(raw) : defaultRef.current };
      } catch {
        cacheRef.current = { raw, parsed: defaultRef.current };
      }
    }
    return cacheRef.current.parsed;
  }, [key]);

  const getServerSnapshot = useCallback((): T => defaultRef.current, []);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const handler = () => onStoreChange();
      window.addEventListener(`ls:${key}`, handler);
      return () => window.removeEventListener(`ls:${key}`, handler);
    },
    [key]
  );

  const value = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const setValue = useCallback(
    (updater: T | ((prev: T) => T)) => {
      const raw = localStorage.getItem(key);
      let current: T;
      try {
        current = raw ? JSON.parse(raw) : defaultRef.current;
      } catch {
        current = defaultRef.current;
      }
      const next =
        typeof updater === "function"
          ? (updater as (prev: T) => T)(current)
          : updater;
      const nextRaw = JSON.stringify(next);
      localStorage.setItem(key, nextRaw);
      cacheRef.current = { raw: nextRaw, parsed: next };
      window.dispatchEvent(new Event(`ls:${key}`));
    },
    [key]
  );

  return [value, setValue];
}

// ---------------------------------------------------------------------------
// CustomerInfo provider
// ---------------------------------------------------------------------------
const STORAGE_KEY = "customer-info";

type CustomerInfoContextValue = {
  customerInfo: CustomerInfo;
  setCustomerInfo: (info: CustomerInfo) => void;
  patchCustomerInfo: (patch: Partial<CustomerInfo>) => void;
};

const CustomerInfoContext = createContext<CustomerInfoContextValue | null>(null);

export function CustomerInfoProvider({ children }: { children: ReactNode }) {
  const [info, setInfo] = useLocalStorage<CustomerInfo>(STORAGE_KEY, {
    name: "",
    address: "",
  });

  const setCustomerInfo = useCallback(
    (newInfo: CustomerInfo) => setInfo(newInfo),
    [setInfo]
  );

  const patchCustomerInfo = useCallback(
    (patch: Partial<CustomerInfo>) =>
      setInfo((prev) => ({ ...prev, ...patch })),
    [setInfo]
  );

  return (
    <CustomerInfoContext
      value={{ customerInfo: info, setCustomerInfo, patchCustomerInfo }}
    >
      {children}
    </CustomerInfoContext>
  );
}

export function useCustomerInfo() {
  const ctx = useContext(CustomerInfoContext);
  if (!ctx)
    throw new Error("useCustomerInfo must be used within CustomerInfoProvider");
  return ctx;
}

// ---------------------------------------------------------------------------
// Saved Addresses provider
// ---------------------------------------------------------------------------
const SAVED_ADDRESSES_KEY = "saved-addresses";

type SavedAddressesContextValue = {
  savedAddresses: SavedAddress[];
  addAddress: (addr: Omit<SavedAddress, "id">) => SavedAddress;
  updateAddress: (id: string, addr: Omit<SavedAddress, "id">) => void;
  deleteAddress: (id: string) => void;
};

const SavedAddressesContext = createContext<SavedAddressesContextValue | null>(
  null
);

export function SavedAddressesProvider({ children }: { children: ReactNode }) {
  const [addresses, setAddresses] = useLocalStorage<SavedAddress[]>(
    SAVED_ADDRESSES_KEY,
    []
  );

  const addAddress = useCallback(
    (addr: Omit<SavedAddress, "id">): SavedAddress => {
      const newAddr: SavedAddress = { ...addr, id: crypto.randomUUID() };
      setAddresses((prev) => [...prev, newAddr]);
      return newAddr;
    },
    [setAddresses]
  );

  const updateAddress = useCallback(
    (id: string, addr: Omit<SavedAddress, "id">) => {
      setAddresses((prev) =>
        prev.map((a) => (a.id === id ? { ...addr, id } : a))
      );
    },
    [setAddresses]
  );

  const deleteAddress = useCallback(
    (id: string) => {
      setAddresses((prev) => prev.filter((a) => a.id !== id));
    },
    [setAddresses]
  );

  return (
    <SavedAddressesContext
      value={{ savedAddresses: addresses, addAddress, updateAddress, deleteAddress }}
    >
      {children}
    </SavedAddressesContext>
  );
}

export function useSavedAddresses() {
  const ctx = useContext(SavedAddressesContext);
  if (!ctx)
    throw new Error(
      "useSavedAddresses must be used within SavedAddressesProvider"
    );
  return ctx;
}
